import { FilterValues } from "@kaltura/mediaspace-shared-types";
import { FilterBarButtonWithModal, SearchBar } from "@kaltura/mediaspace-shared-search";
import { Suspense, useMemo, useState } from "react";
import {
    baseUrl,
    extractDefaultFilterValues,
    fetchKmsData,
    isSearchFilterValues,
    removeEmptyDefaultFilterValues,
    translate,
} from "@kaltura/mediaspace-shared-utils";
import { MediaContainerSkeleton } from "./skeleton/MediaContainerSkeleton";
import { Stack } from "@kaltura/mediaspace-shared-styled";
import { ErrorBoundary } from "@kaltura/mediaspace-shared-ui";
import { GalleryMediaContainerProps, GalleryMediaSearchResponse } from "./GalleryMediaContainerProps";
import { GalleryMedia } from "./GalleryMedia";

/**
 * The `GalleryMediaContainer` component renders media content for galleries in either a grid or list view.
 * It also includes a search bar for filtering media content and a pagination.
 */
export const GalleryMediaContainer = (props: GalleryMediaContainerProps) => {
    const { isGridView, filters, filterMenuProps, content, categoryId, pageSize } = props;

    const [showInitialData, setShowInitialData] = useState(true);

    const [page, setPage] = useState(1);
    const handlePageChange = (page: number) => {
        setPage(page);
        setShowInitialData(false);
    };

    const [filterValues, setFilterValues] = useState(() => extractDefaultFilterValues(filters));
    const minFilterValues = useMemo(
        () => removeEmptyDefaultFilterValues(filters, filterValues),
        [filters, filterValues]
    );

    const handleFilterChange = (values: Partial<FilterValues>) => {
        setFilterValues((filterValues) => ({ ...filterValues, ...values }));
        setPage(1);
        setShowInitialData(false);
    };
    const handleKeywordSearchSubmit = (value: string) => handleFilterChange({ keyword: value });

    const dataFetcher = useMemo<() => GalleryMediaSearchResponse>(
        () =>
            showInitialData
                ? () => content
                : fetchKmsData<GalleryMediaSearchResponse>(
                      `${baseUrl}/default/gallery-ds/search-media/categoryId/${categoryId}`,
                      { ...minFilterValues, page, pagesize: pageSize },
                      true
                  ),
        [showInitialData, content, categoryId, minFilterValues, page, pageSize]
    );

    return (
        <Stack direction={"column"} gap={3}>
            <SearchBar
                buttons={[
                    <FilterBarButtonWithModal filters={filters} values={filterValues} onChange={handleFilterChange} />,
                ]}
                keywordSearchProps={{
                    onSubmit: handleKeywordSearchSubmit,
                    value: filterValues["keyword"] as string,
                    placeholder: translate("Search in Media"),
                    ariaLabel: translate("Search in Media"),
                }}
                filterMenuProps={
                    filterMenuProps && {
                        ...filterMenuProps,
                        values: filterValues,
                        onChange: handleFilterChange,
                    }
                }
            />

            <ErrorBoundary>
                <Suspense fallback={<MediaContainerSkeleton isGridView={isGridView} />}>
                    <GalleryMedia
                        fetchData={dataFetcher}
                        isSearch={isSearchFilterValues(minFilterValues)}
                        page={page}
                        onPageChange={handlePageChange}
                        {...props}
                    />
                </Suspense>
            </ErrorBoundary>
        </Stack>
    );
};
